/**
 * Estado da janela da sala fora da guia (ver `popoutWindow`). A janela fecha sozinha quando a sala
 * sai da tela (a chamada acabou) e, se a pessoa fechar pelo "voltar para a guia" do navegador, a sala
 * volta para a guia.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { POPOUT_SIZE, isPopoutSupported, openPopoutWindow } from './popoutWindow';

/**
 * Quanto a janela deve encolher para voltar ao tamanho de antes da gaveta. O navegador limita o crescimento (borda da
 * tela, tamanho máximo da janela flutuante) e às vezes ignora o pedido, então encolher o que se pediu para crescer
 * deixaria a janela MENOR do que era (foi o que cortava o vídeo): nunca passa do que a janela de fato cresceu.
 * @param {{ outerHeight: number }} win @param {number} asked quanto falta devolver @param {number} base altura de antes
 */
function shrinkBy(win, asked, base) {
    const height = win.outerHeight;
    if (!Number.isFinite(height) || !Number.isFinite(base)) return asked;
    return Math.min(asked, Math.max(0, height - base));
}

/**
 * @param {{ extraHeight?: number }} [options] `extraHeight`: altura que a janela precisa a mais agora (a gaveta da
 *   transcrição aberta). A janela cresce e encolhe junto, para o vídeo não perder tamanho.
 * @returns {{ supported: boolean, isOpen: boolean, container: HTMLElement | null, open: () => Promise<boolean>, close: () => void, toggle: () => void }}
 */
export function usePopoutWindow({ extraHeight = 0 } = {}) {
    const [popout, setPopout] = useState(null);
    const current = useRef(null);
    const opening = useRef(false);
    const supported = isPopoutSupported();
    // Altura extra que a janela aberta tem agora, a altura de antes dela (para nunca encolher abaixo) e a pedida agora.
    const applied = useRef(0);
    const base = useRef(NaN);
    const wanted = useRef(extraHeight);
    wanted.current = extraHeight;

    const close = useCallback(() => {
        current.current?.window.close();
    }, []);

    const open = useCallback(async () => {
        if (current.current || opening.current || !isPopoutSupported()) return false;
        opening.current = true;
        try {
            // Precisa do gesto do usuário (o clique no botão): sem ele o navegador recusa.
            const next = await openPopoutWindow({ width: POPOUT_SIZE.width, height: POPOUT_SIZE.height + wanted.current });
            applied.current = wanted.current;
            base.current = next.window.outerHeight - applied.current;
            // `pagehide` cobre o X, o "voltar para a guia" do navegador e o `close()` daqui.
            next.window.addEventListener('pagehide', () => {
                if (current.current !== next) return;
                current.current = null;
                setPopout(null);
            }, { once: true });
            current.current = next;
            setPopout(next);
            return true;
        } catch {
            // Sem gesto do usuário, ou o navegador recusou: a sala simplesmente fica na guia.
            return false;
        } finally {
            opening.current = false;
        }
    }, []);

    const toggle = useCallback(() => (current.current ? close() : open()), [close, open]);

    // A gaveta da transcrição abre e fecha: a janela cresce e encolhe (o clique que a abriu acabou de acontecer, e é ele
    // que dá ao navegador o direito de redimensionar).
    useEffect(() => {
        const open = current.current;
        if (!open) return;
        const win = open.window;
        let delta = extraHeight - applied.current;
        if (delta === 0) return;
        if (delta > 0 && applied.current === 0) base.current = win.outerHeight;
        if (delta < 0) delta = -shrinkBy(win, -delta, base.current);
        try {
            if (delta !== 0) win.resizeBy(0, delta);
            applied.current = extraHeight;
        } catch { /* o navegador não deixou redimensionar: a gaveta cabe como der na janela atual */ }
    }, [popout, extraHeight]);

    useEffect(() => () => {
        const open = current.current;
        current.current = null;
        open?.window.close();
    }, []);

    return useMemo(
        () => ({
            supported, isOpen: Boolean(popout), container: popout?.container ?? null, open, close, toggle,
        }),
        [supported, popout, open, close, toggle],
    );
}
